import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * Debounced user typeahead.
 *
 * Replaces the raw "paste a user_id here" inputs that used to live on
 * the user-progress, audit-log filter, and member-assignment pages.
 * Accepts a name fragment, an email fragment, or a literal user id and
 * matches against any of them — the backend does the actual matching
 * via the identity provider's user-list endpoint.
 *
 * Props
 *   - value               currently-selected user_id (controlled).
 *   - onChange(user_id)   called with the selected id, or '' on clear.
 *   - onSelect(user)      optional — full row ({ user_id, display_name,
 *                         email, avatar_url }) on a pick.  Use this when
 *                         the caller wants the email/avatar in addition
 *                         to the id.
 *   - api                 shared api client (createApiClient(getToken)).
 *   - placeholder         override placeholder copy.
 *   - autoFocus           focus the input on mount.
 *   - disabled            disable the input + clear button.
 *   - hint                override the empty-results hint copy.
 *
 * Behaviour
 *   - Empty query → shows the hint "Type to search by name, email,
 *     or id" rather than firing a no-op request.
 *   - 200 ms debounce on input — a teammate's burst typing ("barr" →
 *     "barri" → "barrin") fans out as one request per pause, not one
 *     per keystroke.
 *   - Arrow / Enter keyboard navigation through the dropdown.
 *   - Picking a row stamps the input with the user's display name +
 *     id (so the field doesn't look empty after a pick), and fires
 *     onChange with the bare user_id so the parent fetches against
 *     the canonical id, not the human-readable label.
 *
 * Doesn't try to be a full Combobox (no portal, no ARIA listbox).
 * That's fine — the surface is small and the dashboard already uses
 * non-portal popovers (TenantPicker, ProjectPicker).
 */
export function UserSearchInput({
  value = '',
  onChange,
  onSelect,
  api,
  placeholder = 'Search by name, email, or id…',
  autoFocus = false,
  disabled = false,
  hint = 'Type to search by name, email, or id.',
  currentUser = null,
}) {
  const listId = useId();
  // The input shows the human-friendly label after a pick; we keep
  // it separate from `value` so the parent's bare-id contract is
  // preserved while the user sees something meaningful.
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // ── Debounced fetch ──────────────────────────────────────────────
  // 200 ms feels snappy for a typeahead — long enough to coalesce
  // burst typing, short enough that "barri" → results before the
  // user starts thinking "did I break it?".
  const search = useCallback(async (q) => {
    if (!api) return;
    const trimmed = (q || '').trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await api(`/v1/admin/users/search?q=${encodeURIComponent(trimmed)}&limit=20`);
      // The backend wraps in { items: [...] }; the api client unwraps
      // the envelope so we get the bare object here.
      setResults(resp?.items || []);
    } catch (e) {
      setError(e.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { search(query); }, 200);
    return () => clearTimeout(t);
  }, [query, open, search]);

  // ── Outside-click dismissal ──────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActive(-1);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // ── Sync external value resets back into the input ───────────────
  // If the parent clears `value` (e.g. on Clear filters), reflect
  // that in the visible query.  We only react to a clear because
  // arbitrary external changes would clobber what the user is
  // typing.
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  function pick(row) {
    if (!row) return;
    const label = row.display_name
      ? `${row.display_name} (${row.user_id})`
      : row.user_id;
    setQuery(label);
    setOpen(false);
    setActive(-1);
    if (onChange) onChange(row.user_id);
    if (onSelect) onSelect(row);
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        if (query.trim()) search(query);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && results[active]) {
        pick(results[active]);
      } else if (results.length === 1) {
        // One match + Enter = pick it.  Saves a hand to the mouse.
        pick(results[0]);
      } else if (looksLikeUserId(query.trim())) {
        // Bare id pasted, no upstream match (e.g. identity-provider
        // outage).  Honour the literal input so the page can still
        // look it up.
        const id = query.trim();
        setOpen(false);
        if (onChange) onChange(id);
        if (onSelect) onSelect({ user_id: id, display_name: '', email: '', avatar_url: '' });
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  }

  function onClear() {
    setQuery('');
    setResults([]);
    setActive(-1);
    if (onChange) onChange('');
    if (inputRef.current) inputRef.current.focus();
  }

  const showEmptyHint = open && !loading && results.length === 0 && !query.trim();
  const showNoMatch = open && !loading && results.length === 0 && Boolean(query.trim()) && !error;

  return (
    <div className="user-search" ref={wrapperRef}>
      <div className="user-search__inputrow">
        <input
          ref={inputRef}
          type="search"
          name={`user-typeahead-${listId}`}
          className="input user-search__input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          inputMode="search"
          data-1p-ignore="true"
          data-lpignore="true"
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        {currentUser?.user_id && !query && !disabled && (
          <button
            type="button"
            className="user-search__findme"
            onClick={() => pick({
              user_id: currentUser.user_id,
              display_name: currentUser.display_name || '',
              email: currentUser.email || '',
              avatar_url: currentUser.avatar_url || '',
            })}
            title="Look up your own progress"
          >
            Find me
          </button>
        )}
        {query && !disabled && (
          <button
            type="button"
            className="user-search__clear"
            onClick={onClear}
            aria-label="Clear search"
            title="Clear"
          >
            x
          </button>
        )}
      </div>

      {open && (
        <div className="user-search__popover" id={listId} role="listbox">
          {loading && (
            <div className="user-search__row user-search__row--meta">Searching…</div>
          )}
          {error && (
            <div className="user-search__row user-search__row--error">{error}</div>
          )}
          {showEmptyHint && (
            <div className="user-search__row user-search__row--meta">{hint}</div>
          )}
          {showNoMatch && (
            <div className="user-search__row user-search__row--meta">
              No matches.
              {looksLikeUserId(query.trim()) && (
                <> Press Enter to use <code>{query.trim()}</code> as a literal id.</>
              )}
            </div>
          )}
          {results.map((row, i) => (
            <button
              type="button"
              key={row.user_id}
              role="option"
              aria-selected={i === active}
              className={
                'user-search__row user-search__row--pick'
                + (i === active ? ' user-search__row--active' : '')
              }
              onMouseDown={(e) => { e.preventDefault(); pick(row); }}
              onMouseEnter={() => setActive(i)}
            >
              {row.avatar_url ? (
                <img
                  className="user-search__avatar"
                  src={row.avatar_url}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <span className="user-search__avatar user-search__avatar--blank">
                  {(row.display_name || row.email || row.user_id || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="user-search__meta">
                <span className="user-search__name">
                  {row.display_name || row.email || row.user_id}
                </span>
                <span className="user-search__sub">
                  {[row.email, row.user_id].filter(Boolean).join(' . ')}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      <style>{USER_SEARCH_CSS}</style>
    </div>
  );
}

function looksLikeUserId(s) {
  // Bare-id heuristic — same shape the backend identity hydrator
  // uses.  Lets a power user paste a literal id and have the input
  // accept it even when the upstream provider returns no match
  // (outage, rate-limited, etc).
  return typeof s === 'string' && s.startsWith('user_') && s.length >= 10;
}

export const USER_SEARCH_CSS = `
  .user-search { position: relative; }
  .user-search__inputrow { position: relative; display: flex; align-items: stretch; }
  .user-search__input { width: 100%; padding-right: 30px; }
  .user-search__clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border-radius: 4px;
    border: none;
    background: transparent;
    color: var(--text-3);
    cursor: pointer;
    line-height: 1;
    font-size: 14px;
  }
  .user-search__clear:hover { background: var(--surface-1); color: var(--text-1); }
  .user-search__findme {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid var(--border-1);
    background: var(--surface-0);
    color: var(--text-2);
    cursor: pointer;
    font-size: 11.5px;
    font-weight: 500;
    line-height: 1;
  }
  .user-search__findme:hover { background: var(--surface-1); color: var(--text-1); }

  .user-search__popover {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 30;
    background: var(--surface-0);
    border: 1px solid var(--border-1);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    max-height: 320px;
    overflow-y: auto;
  }
  .user-search__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    width: 100%;
    text-align: left;
    border: none;
    background: transparent;
    cursor: default;
    color: var(--text-1);
    font-size: 13px;
  }
  .user-search__row--pick { cursor: pointer; }
  .user-search__row--active,
  .user-search__row--pick:hover {
    background: var(--surface-1);
  }
  .user-search__row--meta {
    color: var(--text-3);
    font-style: italic;
    cursor: default;
  }
  .user-search__row--error { color: var(--danger-700); }
  .user-search__avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }
  .user-search__avatar--blank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--surface-2, var(--surface-1));
    color: var(--text-2);
    font-weight: 600;
    font-size: 12px;
  }
  .user-search__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }
  .user-search__name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .user-search__sub {
    color: var(--text-3);
    font-size: 11.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
